<template>
  <div class="article-page">
    <div class="article-nav">
      <router-link to="/blog" class="back-link">← {{ $t('blog.backToList') }}</router-link>
    </div>

    <article class="article-content" v-if="articleExists">
      <div class="article-header">
        <div class="article-meta">
          <span class="article-category">{{ $t('blog.articles.' + articleId + '.category') }}</span>
          <span class="article-date">{{ $t('blog.articles.' + articleId + '.date') }}</span>
          <span class="article-read-time">{{ $t('blog.articles.' + articleId + '.readTime') }}</span>
        </div>
        <h1>{{ $t('blog.articles.' + articleId + '.title') }}</h1>
        <p class="article-summary">{{ $t('blog.articles.' + articleId + '.summary') }}</p>
      </div>

      <div class="article-body">
        <template v-for="(section, index) in $t('blog.articles.' + articleId + '.sections')" :key="index">
          <h2 v-if="section.heading">{{ section.heading }}</h2>
          <p v-if="section.text">{{ section.text }}</p>
          <ul v-if="section.list">
            <li v-for="(item, i) in section.list" :key="i">{{ item }}</li>
          </ul>
          <div v-if="section.code" class="code-block">
            <pre><code>{{ section.code }}</code></pre>
          </div>
        </template>
      </div>

      <div class="article-author">
        <div class="author-info">
          <strong>{{ $t('blog.authorName') }}</strong>
          <p>{{ $t('blog.authorBio') }}</p>
        </div>
      </div>
    </article>

    <div v-else class="not-found">
      <h2>{{ $t('blog.notFound') }}</h2>
      <router-link to="/blog">{{ $t('blog.backToList') }}</router-link>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BlogArticle',
  computed: {
    articleId() {
      return this.$route.params.id
    },
    articleExists() {
      const validIds = ['json-guide', 'timestamp-explained', 'mortgage-tips', 'markdown-writing', 'image-optimization']
      return validIds.includes(this.articleId)
    }
  }
}
</script>

<style scoped>
.article-page {
  max-width: 800px;
  margin: 0 auto;
}

.article-nav {
  margin-bottom: 24px;
}

.back-link {
  color: var(--accent-color);
  text-decoration: none;
  font-size: 14px;
  transition: opacity 0.2s;
}

.back-link:hover {
  opacity: 0.8;
}

.article-content {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 40px;
  box-shadow: var(--shadow-md);
}

.article-header {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
}

.article-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.article-category {
  background: var(--accent-light);
  color: var(--accent-color);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.article-date,
.article-read-time {
  color: var(--text-muted);
  font-size: 13px;
}

.article-header h1 {
  font-size: 28px;
  color: var(--text-primary);
  margin-bottom: 12px;
  line-height: 1.4;
}

.article-summary {
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.7;
}

.article-body h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 32px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.article-body h2:first-child {
  margin-top: 0;
}

.article-body p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.9;
  margin-bottom: 16px;
}

.article-body ul {
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
}

.article-body li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.article-body li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}

.code-block {
  background: var(--bg-tertiary);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  overflow-x: auto;
}

.code-block pre {
  margin: 0;
}

.code-block code {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px;
  color: var(--text-primary);
  line-height: 1.6;
}

.article-author {
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
}

.author-info strong {
  color: var(--text-primary);
  font-size: 15px;
}

.author-info p {
  color: var(--text-secondary);
  font-size: 14px;
  margin-top: 4px;
}

.not-found {
  text-align: center;
  padding: 60px 20px;
}

.not-found h2 {
  color: var(--text-primary);
  margin-bottom: 16px;
}

.not-found a {
  color: var(--accent-color);
}

@media (max-width: 768px) {
  .article-content {
    padding: 24px;
  }

  .article-header h1 {
    font-size: 22px;
  }
}
</style>
